/** Registry binding for fused hybrid knowledge-base retrieval (LADR-030). */
export const RETRIEVAL_VECTOR_KB_BINDING = "retrieval.vector_kb";

export const MAX_KNOWLEDGE_SOURCES = 8;
export const MAX_ALTERNATE_QUERIES = 4;
export const MAX_QUESTION_LENGTH = 500;
export const MAX_CANDIDATE_K = 100;
export const DEFAULT_CANDIDATE_K = 20;

export const VECTOR_KB_OUTPUT_SCHEMA = {
  type: "object",
  properties: {
    results: {
      type: "array",
      items: {
        type: "object",
        properties: {
          content: { type: "string" },
          doc_id: { type: "string" },
          doc_name: { type: ["string", "null"] },
          chunk_id: { type: "string" },
          chunk_index: { type: "integer" },
          score: { type: ["number", "null"] },
        },
        required: ["content", "doc_id", "chunk_id", "chunk_index"],
      },
    },
    total_found: { type: "integer" },
    scoring_degraded: { type: "boolean" },
  },
  required: ["results", "total_found", "scoring_degraded"],
};

/** Canonical public knowledge-base org ids (Pacific.Agentic.Api org_schema). */
export const CANONICAL_PUBLIC_KB_IDS = [
  "nz-payroll-legislation",
  "au-federal",
  "au-nsw",
  "au-vic",
  "au-qld",
  "au-wa",
  "au-sa",
  "au-tas",
  "au-act",
  "au-nt",
  "au-modern-awards",
] as const;

export type AgentModelOption = {
  value: string;
  label: string;
  enabled: boolean;
};

export const AGENT_MODEL_OPTIONS: AgentModelOption[] = [
  { value: "gpt-5.4-mini", label: "GPT-5.4 Mini", enabled: true },
  {
    value: "dcs:claude-sonnet-5-5",
    label: "Claude Sonnet 5.5 (DCS)",
    enabled: true,
  },
  { value: "dcs:grok-4.6", label: "Grok 4.6 (DCS)", enabled: true },
  {
    value: "dcs:aws-gpt-6.1-sol",
    label: "AWS GPT 6.1 Sol (DCS)",
    enabled: true,
  },
  {
    value: "dcs:aws-gpt-5.6-terra",
    label: "AWS GPT 5.6 Terra (DCS)",
    enabled: true,
  },
  { value: "dcs:aws-gpt-6-luna", label: "AWS GPT 6 Luna (DCS)", enabled: true },
  { value: "dcs:qwen", label: "Qwen 3.8 27B (DCS)", enabled: true },
  {
    value: "dcs:gemma-4-26ba4b-it",
    label: "Gemma 4 26B A4B IT (DCS)",
    enabled: true,
  },
];

export type VectorKbKnowledgeSource = {
  id: string;
  category: "public" | "internal" | "personal";
};

export type VectorKbConfig = {
  knowledge_sources: VectorKbKnowledgeSource[];
  default_top_k: number;
  max_top_k: number;
  min_score?: number | null;
  max_alternate_queries: number;
  candidate_k: number;
  rerank_pool?: number | null;
  expand_when_empty: boolean;
};

export function defaultVectorKbConfig(
  orgId: string,
  orgCategory: "public" | "internal" | "personal",
): VectorKbConfig {
  return {
    knowledge_sources: [{ id: orgId, category: orgCategory }],
    default_top_k: 10,
    max_top_k: 20,
    min_score: 0.4,
    max_alternate_queries: MAX_ALTERNATE_QUERIES,
    candidate_k: DEFAULT_CANDIDATE_K,
    expand_when_empty: false,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function parseKnowledgeSources(value: unknown): VectorKbKnowledgeSource[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    if (!isRecord(item) || typeof item.id !== "string") {
      return [];
    }
    const { category } = item;
    if (
      category !== "public" &&
      category !== "internal" &&
      category !== "personal"
    ) {
      return [];
    }
    return [{ id: item.id, category }];
  });
}

/** Interpret opaque catalog/grant ``config`` as fused ``retrieval.vector_kb`` settings. */
export function parseVectorKbConfig(config: unknown): VectorKbConfig {
  const defaults = defaultVectorKbConfig("", "internal");
  if (!isRecord(config)) {
    return { ...defaults, knowledge_sources: [], min_score: null, rerank_pool: null };
  }

  const minScore = config.min_score;
  return {
    knowledge_sources: parseKnowledgeSources(config.knowledge_sources),
    default_top_k: parseFiniteNumber(
      config.default_top_k,
      defaults.default_top_k,
    ),
    max_top_k: parseFiniteNumber(config.max_top_k, defaults.max_top_k),
    min_score:
      typeof minScore === "number" && Number.isFinite(minScore)
        ? minScore
        : null,
    max_alternate_queries: parseFiniteNumber(
      config.max_alternate_queries,
      defaults.max_alternate_queries,
    ),
    candidate_k: parseFiniteNumber(config.candidate_k, defaults.candidate_k),
    rerank_pool:
      typeof config.rerank_pool === "number" &&
      Number.isFinite(config.rerank_pool)
        ? config.rerank_pool
        : null,
    expand_when_empty: config.expand_when_empty === true,
  };
}

/** ``max_top_k`` must be ≥ ``default_top_k`` (mirrors API ``RetrievalVectorKbConfig``). */
export function getTopKValidationError(config: VectorKbConfig): string | null {
  if (config.max_top_k < config.default_top_k) {
    return `Max top_k must be at least default top_k (${config.default_top_k}).`;
  }
  return null;
}

/** Inline validation for optional ``rerank_pool`` (must be ≥ 1 and ≥ ``max_top_k`` when set). */
export function getRerankPoolValidationError(
  config: VectorKbConfig,
): string | null {
  const pool = config.rerank_pool;
  if (pool == null) {
    return null;
  }
  if (!Number.isInteger(pool) || pool < 1) {
    return "Rerank pool must be an integer of at least 1.";
  }
  if (pool < config.max_top_k) {
    return `Rerank pool must be at least max top_k (${config.max_top_k}).`;
  }
  return null;
}

export function getVectorKbConfigValidationError(
  config: VectorKbConfig,
): string | null {
  return getTopKValidationError(config) ?? getRerankPoolValidationError(config);
}

export function setDefaultTopK(
  config: VectorKbConfig,
  defaultTopK: number,
): VectorKbConfig {
  const value = Math.max(1, defaultTopK);
  return {
    ...config,
    default_top_k: value,
    max_top_k: Math.max(config.max_top_k, value),
  };
}

export function setMaxTopK(
  config: VectorKbConfig,
  maxTopK: number,
): VectorKbConfig {
  const value = Math.max(1, maxTopK);
  return {
    ...config,
    max_top_k: value,
    default_top_k: Math.min(config.default_top_k, value),
  };
}

/** API config JSON — omit ``rerank_pool`` when blank. */
export function serializeVectorKbConfig(
  config: VectorKbConfig,
): Record<string, unknown> {
  const { rerank_pool, ...rest } = config;
  const serialized: Record<string, unknown> = { ...rest };
  if (rerank_pool != null) {
    serialized.rerank_pool = rerank_pool;
  }
  return serialized;
}

export function formatVectorKbConfigSummary(config: VectorKbConfig): string {
  const parts = [
    `top_k ${config.default_top_k}–${config.max_top_k}`,
    `candidate_k ${config.candidate_k}`,
    `max alternates ${config.max_alternate_queries}`,
    config.expand_when_empty ? "expand when empty on" : "expand when empty off",
  ];
  if (config.rerank_pool != null) {
    parts.push(`rerank pool ${config.rerank_pool}`);
  }
  if (config.min_score != null) {
    parts.push(`min score ${config.min_score}`);
  }
  return parts.join(" · ");
}
